import { FunctionComponent, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { convertSprdLocaleToIsoLocale } from '@/utils/convertSprdLocaleToIsoLocale';
import { t } from '@/utils/i18n';
import { getParams } from '@/utils/params';
import { fetchDeliveryEtaAction } from '@/redux/core/actions/fetchDeliveryEtaAction';
import { getDeliveryEta } from '@/redux/core/getters/getDeliveryEta';
import { StoreDispatch } from '@/redux/types';
import { Truck } from './icons/Truck';
import './DeliveryEta.scss';

interface DeliveryEtaProps {
  className?: string;
  productTypeId: string;
  printTypeIds: string[];
}

export const DeliveryEta: FunctionComponent<DeliveryEtaProps> = ({
  className,
  productTypeId,
  printTypeIds,
}) => {
  const dispatch = useDispatch<StoreDispatch>();
  const deliveryEta = useSelector(getDeliveryEta(productTypeId, printTypeIds));
  const lastDeliveryEta = useRef(deliveryEta);
  const formatter = useMemo(
    () =>
      new Intl.DateTimeFormat(convertSprdLocaleToIsoLocale(getParams().locale), {
        month: 'short',
        day: '2-digit',
      }),
    []
  );

  useEffect(() => {
    // keep the last delivery eta to prevent flicker during load
    lastDeliveryEta.current = deliveryEta;
  }, [deliveryEta]);

  useEffect(() => {
    if (deliveryEta === undefined) {
      (dispatch as StoreDispatch)(fetchDeliveryEtaAction(productTypeId, printTypeIds)).then(
        (loadedDeliveryEta) => {
          // remove delivery eta if request fails
          if (!loadedDeliveryEta) {
            lastDeliveryEta.current = undefined;
          }
        }
      );
    }
  }, [dispatch, deliveryEta, productTypeId, printTypeIds]);

  const visibleDeliveryEta = deliveryEta || lastDeliveryEta.current;
  if (!visibleDeliveryEta) {
    return null;
  }

  const from = new Date(visibleDeliveryEta.earliestDateUTC);
  const to = new Date(visibleDeliveryEta.latestDateUTC);

  let formattedFrom = formatter.format(from);
  let formattedTo = formatter.format(to);

  if (from.getMonth() === to.getMonth()) {
    // a formatter when only the day has no ordinal (e.g. dot after the number in german)
    //  so we get the parts, remove the month, and put it back together
    const removeMonth = (date: Date) =>
      formatter
        .formatToParts(date)
        .filter(({ type }) => type !== 'month')
        .map(({ value }) => value)
        .join('');

    if (formattedFrom.match(/^\d+/)) {
      formattedFrom = removeMonth(from);
    } else {
      formattedTo = removeMonth(to);
    }
  }

  return (
    <div className={className}>
      <div className="delivery-eta">
        <Truck width="1.5em" height="1.5em" />
        <span className="delivery-eta__label">{t('embroidery.labels.shippingDate')}:</span>
        <span>
          {formattedFrom} - {formattedTo}
        </span>
      </div>
    </div>
  );
};
